import { useState } from 'react';
import { Box, HStack, Image, Text, VStack, BoxProps } from '@chakra-ui/react';

interface TasteMatchPillProps {
  /** Icon/emoji asset representing the current tier */
  emojiSrc: string;
  emojiAlt?: string;
  /** Tier accent color for the emoji chip background */
  tierColor: string;
  /**
   * Tier level label, e.g. "High", "Good", "Moderate" — shown on desktop hover
   * as "{tierLabel} {matchLabel}". When there is no real tier to name, pass the
   * same copy as `matchLabel` (e.g. both "Still Learning") and the pill renders
   * it once rather than doubling it.
   */
  tierLabel: string;
  /** Label prefix for the hover state, e.g. "TasteMatch" or "Group TasteMatch" */
  matchLabel?: string;
  /**
   * Whether to append the ™ symbol to `matchLabel` in the collapsed state, the
   * hover/focus text, and the aria-label.
   *
   * Defaults to true, for labels ending in the TasteMatch™ trademark. Pass
   * `false` for non-trademarked status/fallback copy (e.g. "Still Learning")
   * that reuses this pill's layout — appending ™ there is both incorrect
   * branding and factually wrong, since there's no match to badge.
   *
   * Per paire-wiki/Marketing/Brand/Trademarked-Terms.md, **TasteMatch is the
   * only trademark in its family**: "Personal TasteMatch" and "Group
   * TasteMatch" are two-word qualified usages of it, and the fused forms
   * "PersonalTasteMatch™"/"GroupTasteMatch™" are explicitly listed as
   * incorrect. Callers passing a qualified label should therefore pass the
   * two-word form ("Group TasteMatch"), not the fused one.
   *
   * TODO(branding): the collaborative surfaces still pass the fused
   * "GroupTasteMatch" (see GroupCompatibilityBadge.getGroupTasteMatchPillProps
   * and CreateCollaborativeListModal). Pre-existing, and correcting member-
   * facing copy needs CD sign-off — tracked separately, not fixed here.
   */
  showTrademark?: boolean;
  onClick?: () => void;
  /** Positioning overrides — defaults to top-right of the containing relatively-positioned box */
  positionProps?: BoxProps;
}

/**
 * Standard card-level tier badge: an opaque card-token pill, top-right, showing
 * "{matchLabel}™" + tier emoji by default. Desktop hover/focus swaps the text to
 * the tier level ("{tierLabel} {matchLabel}™", ™ only when `showTrademark`); click/tap is left to the caller
 * (e.g. opening CompatibilityModal). Set `showTrademark={false}` when
 * `matchLabel` isn't actually a TasteMatch-family term (e.g. "Still Learning").
 */
export function TasteMatchPill({
  emojiSrc,
  emojiAlt = 'TasteMatch',
  tierColor,
  tierLabel,
  matchLabel = 'TasteMatch',
  showTrademark = true,
  onClick,
  positionProps,
}: TasteMatchPillProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const showTierLabel = isHovered || isFocused;

  // Both label strings are derived once here and reused by the visible <Text>
  // and the aria-label, so the sighted and assistive-tech copy can never drift.
  //
  // `expandedLabel` collapses the tier prefix when it is the same copy as
  // `matchLabel`: the no-score fallback passes "Still Learning" for both (there
  // is no tier to name), and naively concatenating would read "Still Learning
  // Still Learning" on hover and to a screen reader.
  //
  // Both labels carry the ™ when `showTrademark` is set, so the hover/focus text
  // and the aria-label match the collapsed copy's branding.
  const trademark = showTrademark ? '™' : '';
  const expandedLabel =
    (tierLabel && tierLabel !== matchLabel ? `${tierLabel} ${matchLabel}` : matchLabel) + trademark;
  const collapsedLabel = `${matchLabel}${trademark}`;

  const activate = () => onClick?.();

  return (
    <HStack
      position="absolute"
      top={2}
      right={2}
      zIndex={2}
      spacing={2}
      bg="var(--pill-bg)"
      borderRadius="full"
      pl={3}
      pr={1}
      py={1}
      boxShadow="0 2px 8px var(--color-overlay-30)"
      cursor={onClick ? 'pointer' : 'default'}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? `${expandedLabel} — view compatibility details` : expandedLabel}
      onClick={(e) => {
        e.stopPropagation();
        activate();
      }}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                activate();
              }
            }
          : undefined
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      {...positionProps}
    >
      <VStack spacing={0} align="flex-end">
        <Text fontSize="xs" color="var(--pill-text)" fontWeight="bold" lineHeight={1.2} whiteSpace="nowrap">
          {showTierLabel ? expandedLabel : collapsedLabel}
        </Text>
      </VStack>
      <Box bg={tierColor} borderRadius="full" p="4px">
        <Image src={emojiSrc} alt={emojiAlt} boxSize="30px" objectFit="contain" />
      </Box>
    </HStack>
  );
}

export default TasteMatchPill;
